import { atom } from 'nanostores';
import { PAGE_TEMPLATE } from '@/enums/pageTemplate';
import { localeStore } from '@/locale';
import { validateEmail } from '@/lib/validator/validateEmail';
import { getGaIds, trackEvent } from '@/utils/analytics';

const Settings = {
  promoPattern: /^[a-z0-9_-]{1,32}$/i,
  paymentPollDelay: 5000,
  errors: {
    promoEmpty: 'Enter a promo code, or continue without one.',
    promoInvalid: 'This promo code is not valid for this order. Check it or remove it.',
    promoExpired: 'This promo code has expired. Try another code or remove it.',
    promoUsed: 'This promo code has already been used. Try another code or remove it.',
    promoFormat: 'Use letters, numbers, hyphens or underscores (up to 32 characters).',
    emailRequired: 'Enter an email address to receive your order.',
    emailInvalid: 'Enter a valid email address, such as name@company.com.',
    shortText: 'Use 200 characters or fewer.',
    country: 'Choose a country from the list.',
    payment: 'Could not connect to the payment provider. Your data is saved; try again.',
  },
};

const en = {
  label: 'Review & pay',
  title: 'Review & pay',
};

export class Step3 {
  id = '3';

  locale = { en, ru: en };

  $promo = atom('');

  $appliedPromo = atom('');

  $email = atom('');

  $name = atom('');

  $company = atom('');

  $country = atom('');

  $calculatedPrice = atom(null);

  $paymentLink = atom('');

  $paymentState = atom('idle');

  $paymentStatusUrl = atom('');

  $paymentError = atom('');

  $isCheckingPayment = atom(false);

  $isBillingOpen = atom(false);

  $isBreakdownOpen = atom(false);

  $isPriceLoading = atom(false);

  $isSubmitting = atom(false);

  $priceError = atom('');

  $promoError = atom('');

  $emailError = atom('');

  $nameError = atom('');

  $companyError = atom('');

  $countryError = atom('');

  priceRequestId = 0;

  submissionId = 0;

  requestedPromo = '';

  paymentTimer = null;

  store;

  constructor(store) {
    this.store = store;
  }

  onEnter() {
    if (this.$paymentState.get() === 'pending') this.checkPaymentStatus();
    else if (this.$paymentState.get() === 'idle') this.calculatePrice();
  }

  onLeave() {
    window.clearTimeout(this.paymentTimer);
  }

  onTariffChange() {
    this.onReset();
  }

  onReset() {
    this.onLeave();
    this.priceRequestId += 1;
    this.submissionId += 1;
    this.requestedPromo = '';
    this.$promo.set('');
    this.$appliedPromo.set('');
    this.$email.set('');
    this.$name.set('');
    this.$company.set('');
    this.$country.set('');
    this.$calculatedPrice.set(null);
    this.$paymentLink.set('');
    this.$paymentState.set('idle');
    this.$paymentStatusUrl.set('');
    this.$paymentError.set('');
    this.$isCheckingPayment.set(false);
    this.$isBillingOpen.set(false);
    this.$isBreakdownOpen.set(false);
    this.$isPriceLoading.set(false);
    this.$isSubmitting.set(false);
    this.clearErrors();
  }

  clearErrors() {
    this.$priceError.set('');
    this.$promoError.set('');
    this.$emailError.set('');
    this.$nameError.set('');
    this.$companyError.set('');
    this.$countryError.set('');
  }

  async onValidate() {
    if (this.$isSubmitting.get() || this.$isPriceLoading.get() || this.$priceError.get()
      || !this.$calculatedPrice.get() || !['idle', 'connection-error'].includes(this.$paymentState.get())) return false;
    const { orderStore } = this.store;
    if (!await orderStore.step1.onValidate()) {
      orderStore.setStepById('1');
      return false;
    }
    if (!await orderStore.step2.onValidate()) {
      orderStore.setStepById('2');
      return false;
    }
    const email = this.$email.get().trim();
    let emailError = '';
    if (!email) emailError = Settings.errors.emailRequired;
    else if (!validateEmail(email)) emailError = Settings.errors.emailInvalid;
    this.$emailError.set(emailError);
    this.$nameError.set(this.$name.get().length > 200 ? Settings.errors.shortText : '');
    this.$companyError.set(this.$company.get().length > 200 ? Settings.errors.shortText : '');
    this.$countryError.set(this.$country.get() && !/^[A-Z]{2}$/.test(this.$country.get()) ? Settings.errors.country : '');

    const fields = [
      ['email', this.$emailError.get()],
      ['billing-name', this.$nameError.get()],
      ['billing-company', this.$companyError.get()],
      ['billing-country', this.$countryError.get()],
    ];
    const firstError = fields.find(([, error]) => error);
    if (firstError) {
      if (firstError[0].startsWith('billing-')) this.$isBillingOpen.set(true);
      this.store.orderStore.focusField(firstError[0]);
      return false;
    }
    await this.createOrder();
    return false;
  }

  setPromo(value) {
    this.$promo.set(value);
    this.$promoError.set('');
    this.store.orderStore.scheduleDraftSave();
  }

  applyPromo = async () => {
    const promo = this.$promo.get().trim();
    if (!promo) {
      this.$promoError.set(Settings.errors.promoEmpty);
      this.store.orderStore.focusField('promo');
      return;
    }
    if (!Settings.promoPattern.test(promo)) {
      this.$promoError.set(Settings.errors.promoFormat);
      this.store.orderStore.focusField('promo');
      return;
    }
    this.$promo.set(promo);
    const applied = await this.calculatePrice(promo);
    if (applied) {
      trackEvent('coupon_apply', { coupon_result: 'success' });
      this.store.orderStore.scheduleDraftSave();
    }
  };

  removePromo = () => {
    this.$promo.set('');
    this.$appliedPromo.set('');
    this.$promoError.set('');
    this.calculatePrice();
    this.store.orderStore.scheduleDraftSave();
  };

  getPromoError(error) {
    const message = `${error?.response?.data?.message || ''}`.toLowerCase();
    if (message.includes('expired')) return Settings.errors.promoExpired;
    if (message.includes('used')) return Settings.errors.promoUsed;
    return Settings.errors.promoInvalid;
  }

  async calculatePrice(promoToApply = '') {
    const tariff = this.store.orderStore.$tariff.get();
    if (!tariff) return false;
    this.priceRequestId += 1;
    const requestId = this.priceRequestId;
    const { step1 } = this.store.orderStore;
    const count = step1.floorsCount || 1;
    if (count > 50 || step1.$files.get().some((item) => !Number.isInteger(Number(item.floors)) || Number(item.floors) < 1 || Number(item.floors) > 50)) {
      this.$calculatedPrice.set(null);
      this.$isPriceLoading.set(false);
      this.$priceError.set('Enter valid floor counts to calculate the total.');
      return false;
    }
    const params = {
      tariff_id: tariff.tariff,
      count,
    };
    const promo = promoToApply || this.$appliedPromo.get();
    this.requestedPromo = promo;
    if (promo) params.promocode = promo;
    const email = this.$email.get().trim();
    if (validateEmail(email)) params.email = email;

    this.$calculatedPrice.set(null);
    this.$isPriceLoading.set(true);
    this.$priceError.set('');
    this.$promoError.set('');
    try {
      const { data } = await this.store.apiStore.api.getCalculatedPriceEn(params, { skipErrorRedirect: true });
      if (requestId !== this.priceRequestId) return false;
      if (data.sum === null || data.sum === undefined || !Number.isFinite(Number(data.sum)) || (promo && !data.coupon)) {
        throw new Error('Incomplete price response');
      }
      this.$calculatedPrice.set(data);
      if (promo && (!data.coupon?.valid || data.coupon.code?.toUpperCase() !== promo.toUpperCase())) {
        this.$appliedPromo.set('');
        this.$promoError.set(this.getPromoError({ response: { data: { message: data.coupon?.message } } }));
        trackEvent('coupon_apply', { coupon_result: 'invalid' });
        return false;
      }
      this.$appliedPromo.set(data.coupon?.code || '');
      return true;
    } catch (error) {
      if (requestId === this.priceRequestId) {
        this.$priceError.set('Unable to calculate the total. Try again.');
      }
      return false;
    } finally {
      if (requestId === this.priceRequestId) this.$isPriceLoading.set(false);
    }
  }

  setEmail(value) {
    this.$email.set(value);
    this.$emailError.set('');
    this.store.orderStore.scheduleDraftSave();
    if (this.$appliedPromo.get()) this.calculatePrice();
    else if (this.$isPriceLoading.get() && this.requestedPromo) this.calculatePrice(this.requestedPromo);
  }

  setBillingField(field, value) {
    const fields = {
      name: [this.$name, this.$nameError],
      company: [this.$company, this.$companyError],
      country: [this.$country, this.$countryError],
    };
    const [valueStore, errorStore] = fields[field];
    valueStore.set(value);
    errorStore.set('');
    this.store.orderStore.scheduleDraftSave();
  }

  toggleBilling = () => {
    this.$isBillingOpen.set(!this.$isBillingOpen.get());
  };

  toggleBreakdown = () => {
    this.$isBreakdownOpen.set(!this.$isBreakdownOpen.get());
  };

  async createOrder() {
    if (this.$isSubmitting.get()) return;
    this.submissionId += 1;
    const { submissionId } = this;
    this.$isSubmitting.set(true);
    this.$paymentState.set('processing');
    this.$priceError.set('');

    const { orderStore } = this.store;
    const { step1, step2 } = orderStore;
    const tariff = orderStore.$tariff.get();
    const formData = new FormData();
    formData.append('tariff_id', tariff.tariff);
    formData.append('email', this.$email.get().trim());
    formData.append('customer_type', 'company');
    formData.append('property_type', step1.$files.get()[0]?.propertyType || 'other');
    formData.append('plans_count', step1.floorsCount);

    step1.$files.get().forEach(({ file, propertyType, floors }) => {
      formData.append('plans[]', file);
      formData.append('plan_property_types[]', propertyType);
      formData.append('plan_floors[]', floors);
    });
    step2.$styles.get().forEach((style) => formData.append('styles[]', style));
    formData.append('window_type', step2.$windowType.get());
    if (step2.$hasEnvironmentType.get()) formData.append('environment_type', step2.$environmentType.get());

    const commentParts = [];
    if (step2.$environmentType.get() === 'custom') formData.append('custom_view', step2.$customView.get().trim());
    if (step2.$comment.get()) commentParts.push(step2.$comment.get());
    if (commentParts.length) formData.append('comment', commentParts.join('\n\n'));
    if (step2.$logo.get()) formData.append('widget_branding_logo', step2.$logo.get());
    if (step2.$website.get().trim()) formData.append('widget_branding_url', step2.$website.get().trim());
    if (this.$appliedPromo.get()) formData.append('promocode', this.$appliedPromo.get());
    if (this.$name.get()) formData.append('billing_name', this.$name.get());
    if (this.$company.get()) formData.append('billing_company', this.$company.get());
    if (this.$country.get()) formData.append('billing_country', this.$country.get());

    try {
      const { clientId, sessionId } = await getGaIds();
      if (submissionId !== this.submissionId) return;
      if (clientId) formData.append('ga_client_id', clientId);
      if (sessionId) formData.append('ga_session_id', sessionId);
      const { data } = await this.store.apiStore.api.createOrderModal(formData);
      if (submissionId !== this.submissionId) return;
      const urlTemplate = `{0}&${PAGE_TEMPLATE}&exfo=742`;
      this.$paymentLink.set(localeStore.decode(urlTemplate, data.data.payment_link));
      this.$paymentStatusUrl.set(data.data.payment_status_url || '');
      this.$paymentState.set('pending');
      orderStore.focusField('payment');
      await orderStore.saveDraft();
      this.checkPaymentStatus();
    } catch (error) {
      if (submissionId !== this.submissionId) return;
      const message = `${error?.response?.data?.message || ''}`.toLowerCase();
      if (this.$appliedPromo.get() && (message.includes('code') || message.includes('promo'))) {
        this.$promoError.set(this.getPromoError(error));
        this.$paymentState.set('idle');
        this.store.orderStore.focusField('promo');
        trackEvent('coupon_apply', { coupon_result: 'invalid' });
      } else {
        this.$priceError.set(Settings.errors.payment);
        this.$paymentState.set('connection-error');
      }
    } finally {
      if (submissionId === this.submissionId) this.$isSubmitting.set(false);
    }
  }

  retryPayment = async () => {
    if (!['declined', 'connection-error'].includes(this.$paymentState.get())) return;
    this.onLeave();
    this.$paymentState.set('idle');
    this.$paymentLink.set('');
    this.$paymentStatusUrl.set('');
    this.$paymentError.set('');
    this.store.orderStore.scheduleDraftSave();
    await this.calculatePrice();
  };

  checkPaymentStatus = async () => {
    window.clearTimeout(this.paymentTimer);
    const statusUrl = this.$paymentStatusUrl.get();
    if (this.$isCheckingPayment.get() || this.$paymentState.get() !== 'pending') return;
    if (!statusUrl) {
      this.$paymentError.set('Payment status is unavailable. Keep this payment open and check your confirmation email.');
      return;
    }
    this.$isCheckingPayment.set(true);
    this.$paymentError.set('');
    try {
      const { data } = await this.store.apiStore.api.getOrderPaymentStatus(statusUrl);
      if (statusUrl !== this.$paymentStatusUrl.get()) return;
      if (['success', 'declined', 'refunded'].includes(data.status)) {
        this.$paymentState.set(data.status);
        this.store.orderStore.focusField('payment');
        if (data.status === 'success' || data.status === 'refunded') await this.store.orderStore.clearDraft();
        else await this.store.orderStore.saveDraft();
      }
    } catch (error) {
      if (statusUrl === this.$paymentStatusUrl.get()) {
        this.$paymentError.set('Could not check the payment status. Keep this attempt open and try checking again.');
      }
    } finally {
      if (statusUrl === this.$paymentStatusUrl.get()) {
        this.$isCheckingPayment.set(false);
        const { orderStore } = this.store;
        if (this.$paymentState.get() === 'pending' && orderStore.$isOpened.get() && orderStore.$stepIndex.get() === 2) {
          this.paymentTimer = window.setTimeout(this.checkPaymentStatus, Settings.paymentPollDelay);
        }
      }
    }
  };

  getDraftData() {
    return {
      promo: this.$promo.get(),
      appliedPromo: this.$appliedPromo.get(),
      email: this.$email.get(),
      name: this.$name.get(),
      company: this.$company.get(),
      country: this.$country.get(),
      paymentLink: this.$paymentLink.get(),
      paymentStatusUrl: this.$paymentStatusUrl.get(),
      paymentState: this.$paymentState.get(),
    };
  }

  restoreDraft(data = {}) {
    this.$promo.set(data.promo || '');
    this.$appliedPromo.set(data.appliedPromo || '');
    this.$email.set(data.email || '');
    this.$name.set(data.name || '');
    this.$company.set(data.company || '');
    this.$country.set(data.country || '');
    this.$paymentLink.set(data.paymentLink || '');
    this.$paymentStatusUrl.set(data.paymentStatusUrl || '');
    this.$paymentState.set(data.paymentLink && ['pending', 'declined'].includes(data.paymentState) ? data.paymentState : 'idle');
    this.clearErrors();
  }
}
